import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, ArrowUpRight, Gift, Map, Rocket, Check, UserPlus } from "lucide-react";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = ""; // e.g. "919876543210" — button hidden until set

const OUTSOURCE_CATEGORIES = ["Branding", "Website / App design", "Content services", "Social media management", "Growth consulting", "Training your team in these skills"];

export default function ServicesPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ requirement: "" });
  const [sending, setSending] = useState(false);
  const [me, setMe] = useState(null); // null = checking, false = anonymous, object = user
  const formRef = useRef(null);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setMe)
      .catch(() => setMe(false));
    return () => document.head.removeChild(m);
  }, []);

  const goToAuth = () => {
    sessionStorage.setItem("post_auth_dest", "/services");
    navigate("/tribe");
  };

  const goToForm = (prefill) => {
    setForm((f) => ({ ...f, requirement: f.requirement || prefill }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/api/services/inquiry`, form, { withCredentials: true });
      toast.success("Thanks! Your requirement is in — I'll get back to you soon.");
      setForm({ requirement: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not send — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rp-root rp-min-h-svh relative" style={{ background: "#EDE7D9" }} data-testid="services-page">
      <div className="rp-grain" />
      <header className="max-w-5xl mx-auto px-6 md:px-10 pt-5 pb-2">
        <p className="uppercase" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: "0.72rem", letterSpacing: "0.2em", color: "#8A7E6B" }}>
          Roshan Raykar — Services
        </p>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 pb-24">
        <section className="pt-10 md:pt-16 pb-12">
          <h1 className="rp-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#262016]" data-testid="services-heading">
            Three ways to work <span className="rp-italic" style={{ color: "#B5793F" }}>with me.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-[#5C5042]">
            From free direction to a done-for-you build — pick what fits your time, budget and ambition.
          </p>
        </section>

        <section className="grid md:grid-cols-3 gap-8 items-stretch" data-testid="services-tiers">
          {/* Tier 1 — Free */}
          <article data-testid="tier-free" className="bg-white rounded-2xl p-7 border border-[#26201612] flex flex-col" style={{ boxShadow: "0 1px 0 rgba(38,32,22,.04)" }}>
            <Gift size={22} style={{ color: "#23C795" }} />
            <p className="mt-4 text-xs uppercase tracking-widest" style={{ color: "#23C795" }}>Free</p>
            <h2 className="rp-display text-2xl text-[#262016] mt-1">Know <span className="rp-italic">what</span> to do</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#5C5042]">
              Don't spend on anything. I'll tell you <strong>what</strong> to do and share the right knowledge resources — completely free.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#5C5042]">
              {["Clear direction on your next steps", "Curated knowledge resources", "No cost, no catch"].map((x) => (
                <li key={x} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "#23C795" }} />{x}</li>
              ))}
            </ul>
            <div className="flex-1" />
            <button onClick={() => goToForm("I'd like the free guidance — here's what I'm working on: ")} data-testid="tier-free-cta"
                    className="mt-5 w-full rounded-full px-5 py-2.5 text-sm border border-[#262016] text-[#262016] hover:bg-[#262016] hover:text-[#F6F1E5] transition-colors">
              Ask me what to do
            </button>
          </article>

          {/* Tier 2 — Guided, highlighted */}
          <article data-testid="tier-guided" className="rounded-2xl p-7 flex flex-col relative" style={{ background: "#262016", boxShadow: "0 12px 32px rgba(38,32,22,.25)" }}>
            <span className="absolute top-4 right-5 text-[10px] uppercase tracking-widest rounded-full px-3 py-1" style={{ background: "#B5793F", color: "#F6F1E5" }}>
              Most value
            </span>
            <Map size={22} style={{ color: "#B5793F" }} />
            <p className="mt-4 text-xs uppercase tracking-widest" style={{ color: "#B5793F" }}>Guided DIY</p>
            <h2 className="rp-display text-2xl text-[#F6F1E5] mt-1">Know exactly <span className="rp-italic">how</span></h2>
            <p className="mt-3 text-sm leading-relaxed text-[#B8AD99]">
              Exactly <strong style={{ color: "#F6F1E5" }}>how</strong> to do it, plus help with your brand's custom work — saving your time and effort.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#B8AD99]">
              {["Step-by-step: exactly how to execute", "Custom work for your brand", "Support for queries — 4 weeks"].map((x) => (
                <li key={x} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "#B5793F" }} />{x}</li>
              ))}
            </ul>
            <div className="flex-1" />
            <button onClick={() => goToForm("I'm interested in the guided plan. About my brand: ")} data-testid="tier-guided-cta"
                    className="mt-5 w-full rounded-full px-5 py-2.5 text-sm bg-[#F6F1E5] text-[#262016] hover:opacity-90 transition-opacity">
              Start with my brand
            </button>
          </article>

          {/* Tier 3 — Outsource */}
          <article data-testid="tier-outsource" className="bg-white rounded-2xl p-7 border border-[#26201612] flex flex-col" style={{ boxShadow: "0 1px 0 rgba(38,32,22,.04)" }}>
            <Rocket size={22} style={{ color: "#19B5DE" }} />
            <p className="mt-4 text-xs uppercase tracking-widest" style={{ color: "#19B5DE" }}>Done for you</p>
            <h2 className="rp-display text-2xl text-[#262016] mt-1">Fully <span className="rp-italic">outsourced</span></h2>
            <p className="mt-3 text-sm leading-relaxed text-[#5C5042]">
              Don't want to DIY? I take it end-to-end in these categories:
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#5C5042]">
              {OUTSOURCE_CATEGORIES.map((x) => (
                <li key={x} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "#19B5DE" }} />{x}</li>
              ))}
            </ul>
            <div className="flex-1" />
            <p className="mt-4 text-sm text-[#5C5042]">
              Want to see my portfolio and previous work?{" "}
              <a href="/" target="_blank" rel="noreferrer" data-testid="tier-portfolio-link"
                 className="underline underline-offset-2" style={{ color: "#B5793F" }}>Click here</a>
            </p>
            <button onClick={() => goToForm("I want to fully outsource. Category: Branding / Website-App design / Content / Social media. Details: ")} data-testid="tier-outsource-cta"
                    className="mt-5 w-full rounded-full px-5 py-2.5 text-sm border border-[#262016] text-[#262016] hover:bg-[#262016] hover:text-[#F6F1E5] transition-colors">
              Get a quote
            </button>
          </article>
        </section>

        <section className="mt-10 flex flex-wrap items-center gap-3" data-testid="services-contact">
          <p className="text-sm text-[#5C5042]">Prefer talking first?</p>
          <a href="mailto:hi@rosh.work" data-testid="services-email-btn"
             className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm bg-[#262016] text-[#F6F1E5] hover:opacity-90 transition-opacity">
            <Mail size={15} /> hi@rosh.work
          </a>
          {WHATSAPP_NUMBER && (
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="services-whatsapp-btn"
               className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border border-[#26201640] text-[#262016] hover:bg-[#26201610] transition-colors">
              <MessageCircle size={15} /> WhatsApp
            </a>
          )}
        </section>

        <section ref={formRef} className="mt-16 max-w-2xl scroll-mt-10" data-testid="services-form-section">
          <h2 className="rp-display text-2xl text-[#262016]">Share your requirement</h2>
          {me === null ? (
            <p className="mt-4 text-sm text-[#8A7E6B]">One moment…</p>
          ) : me === false ? (
            <div className="mt-4 rounded-2xl bg-white border border-[#26201612] p-7" data-testid="services-auth-gate">
              <p className="text-sm text-[#5C5042] leading-relaxed">
                To send a brief and keep the conversation in one place, create a free account —
                you'll automatically join <strong>Rosh's Tribe</strong> and can track my replies right from your dashboard (and email).
              </p>
              <button onClick={goToAuth} data-testid="services-signup-btn"
                      className="mt-5 inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm text-[#F6F1E5]"
                      style={{ background: "#262016" }}>
                <UserPlus size={15} /> Create account / Sign in
              </button>
            </div>
          ) : (
            <>
              <p className="mt-2 text-sm text-[#5C5042]">
                Sending as <strong>{me.name || me.email}</strong> ({me.email}) — I read every brief personally and reply from hi@rosh.work.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <textarea required rows={5} value={form.requirement} onChange={(e) => setForm({ requirement: e.target.value })}
                          placeholder="Describe your project, which option you're leaning towards, timeline and budget range…"
                          data-testid="services-form-requirement"
                          className="w-full rounded-xl border border-[#26201620] bg-white px-4 py-3 text-sm text-[#262016] outline-none focus:border-[#B5793F]" />
                <button type="submit" disabled={sending} data-testid="services-form-submit"
                        className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm text-[#F6F1E5] transition-opacity disabled:opacity-50"
                        style={{ background: "#262016" }}>
                  {sending ? "Sending…" : "Send requirement"} <ArrowUpRight size={15} />
                </button>
              </form>
            </>
          )}
        </section>
      </main>

      <footer className="py-8 border-t border-[#26201615] text-center">
        <p className="rp-display rp-italic text-sm text-[#8A7E6B]">© Roshan Raykar</p>
      </footer>
    </div>
  );
}
